// src/pages/demo/DemoLessonPlanDetail.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { lessonPlansAPI, homeworkScheduleAPI, lessonAnalysisAPI, studentAPI } from '../../services/api';
import LessonUploader from '../../components/LessonUploader';
import LessonPlanGuideOverlay from '../../components/LessonPlanGuideOverlay';
import { hasShownThisSession, isProgressiveGuideLoginEligible, markShownThisSession, setGuideUser } from '../../utils/guideSession';
import { usesCustomUploadPortal } from '../../utils/uploadPortal';

// --- BRAND PALETTE ---
const BRAND = {
    teal: '#75B9BE',
    cream: '#FDF6EC',
    charcoal: '#2D4042',
    coral: '#F28482',
    white: '#FFFFFF',
    grey: '#F0F2F5',
    danger: '#E07A5F',
    warning: '#F2CC8F',
    success: '#81C784'
};

// --- SCHEDULE CREATION MODAL ---
const ScheduleCreationModal = ({ isOpen, onClose, onSubmit, existingClasses = [], classesLoading = false, lessonPlanYearGroup = '', lessonPlanSubject = '' }) => {
    const [classMode, setClassMode] = useState('new');
    const [selectedExistingClassId, setSelectedExistingClassId] = useState('');
    const [name, setName] = useState('');

    const selectedExistingClass = existingClasses.find(klass => klass._id === selectedExistingClassId);

    useEffect(() => {
        if (classMode !== 'existing') return;
        setName(selectedExistingClass?.name || '');
    }, [classMode, selectedExistingClassId, existingClasses]);

    useEffect(() => {
        if (classMode !== 'new') return;
        setSelectedExistingClassId('');
    }, [classMode]);

    if (!isOpen) return null;

    const handleSubmit = () => {
        if (classMode === 'existing') {
            if (!selectedExistingClass) {
                alert('Please select an existing class.');
                return;
            }
            onSubmit({ name: selectedExistingClass.name, sourceScheduleId: selectedExistingClass._id });
            return;
        }

        if (!name.trim()) {
            alert('Please enter a class name.');
            return;
        }

        onSubmit({ name });
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(45, 64, 66, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div className="dash-card" style={{ padding: '30px', width: '450px', maxWidth: '90%', background: 'white', borderRadius:'16px', border:`2px solid ${BRAND.charcoal}` }}>
                <h3 style={{marginTop:0, fontSize:'1.5rem', fontFamily:'Nunito, sans-serif', color: BRAND.charcoal}}>Create/Assign Class to Lesson Plan</h3>
                <p style={{marginBottom:'20px', color:'#5D7375'}}>Analysis complete! Please confirm details.</p>

                <div style={{ marginBottom: '20px', padding: '12px 16px', background: '#F0F8F9', borderRadius: '10px', border: '1px solid #B2D8DB', display: 'flex', gap: '24px' }}>
                    <div>
                        <span style={{ fontSize: '0.75em', fontWeight: '700', color: '#5D7375', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Year Group</span>
                        <p style={{ margin: '2px 0 0 0', fontWeight: '700', color: BRAND.charcoal }}>{lessonPlanYearGroup || '—'}</p>
                    </div>
                    <div>
                        <span style={{ fontSize: '0.75em', fontWeight: '700', color: '#5D7375', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Subject</span>
                        <p style={{ margin: '2px 0 0 0', fontWeight: '700', color: BRAND.charcoal }}>{lessonPlanSubject || '—'}</p>
                    </div>
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <label style={{display:'block', marginBottom:'5px', fontSize:'0.9em', fontWeight:'700', color: BRAND.charcoal}}>Class</label>
                    <select
                        className="dash-input"
                        value={classMode === 'new' ? 'new' : selectedExistingClassId}
                        onChange={e => {
                            const value = e.target.value;
                            if (value === 'new') {
                                setClassMode('new');
                                setName('');
                            } else {
                                setClassMode('existing');
                                setSelectedExistingClassId(value);
                            }
                        }}
                        disabled={classesLoading}
                    >
                        <option value="new">New Class</option>
                        {existingClasses.map(klass => (
                            <option key={klass._id} value={klass._id}>
                                {klass.name} ({klass.yearGroup} • {klass.studentCount || 0} students)
                            </option>
                        ))}
                    </select>
                </div>

                {classMode === 'new' && (
                    <div style={{ marginBottom: '25px' }}>
                        <label style={{display:'block', marginBottom:'5px', fontSize:'0.9em', fontWeight:'700', color: BRAND.charcoal}}>Class Name</label>
                        <input className="dash-input" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Year 7 History Class" />
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap:'10px' }}>
                    <button onClick={onClose} className="btn-base" style={{ background: '#f0f0f0', color: BRAND.charcoal }}>Cancel</button>
                    <button onClick={handleSubmit} className="btn-base btn-coral">Add Class</button>
                </div>
            </div>
        </div>
    );
};

const normalizeRegistrationCode = (code) => {
    if (!code) return '';
    return code.toString().trim().replace(/\s+/g, ' ').toUpperCase();
};

const NACRO_PROMPT_OPTIONS = [
    { value: 'NACRO_PROMPT_1', label: 'HSE - Manual Handling at Work' },
    { value: 'NACRO_PROMPT_2', label: 'Custom NACRO Prompt 2' }
];

const getClassRoutePrefix = (isDemoMode) => (isDemoMode ? '/lite/profiles' : '/students');
const getClassRoute = (scheduleId, isDemoMode) => `${getClassRoutePrefix(isDemoMode)}/${scheduleId}`;

const DemoLessonPlanDetail = ({ onLogout, isDemoMode, user }) => {
    const guideUserId = user?.uid || user?.hashedPassword || null;

    useEffect(() => {
        setGuideUser(guideUserId);
    }, [guideUserId]);
    const { id } = useParams();
    const navigate = useNavigate();
    const [lessonPlan, setLessonPlan] = useState(null);
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Workflow State
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [existingClasses, setExistingClasses] = useState([]);
    const [classesLoading, setClassesLoading] = useState(false);
    const [lessonGenerationQueue, setLessonGenerationQueue] = useState({});
    const [assignedSchedules, setAssignedSchedules] = useState([]);
    const [selectedNacroPrompt, setSelectedNacroPrompt] = useState(NACRO_PROMPT_OPTIONS[0].value);
    const [guideConfig, setGuideConfig] = useState({ isOpen: false, mode: 'manual', autoStep: null });
    const registrationCode = user?.registrationCodeUsed || user?.user?.registrationCodeUsed || user?.data?.registrationCodeUsed;
    const NACRO_ENABLED = false; // NACRO portal retired (15 Oct 2026): no custom prompt picker
    const isNacroUser = NACRO_ENABLED && normalizeRegistrationCode(registrationCode) === 'NACRO R_26';
    const hasCustomUploadPortal = usesCustomUploadPortal(registrationCode);
    const isKnowledgeOrganiserPlan = lessonPlan?.platformMode === 'knowledge_organiser';
    const useSingleLessonUploadFlow = hasCustomUploadPortal || isKnowledgeOrganiserPlan;
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setClassesLoading(true);
            const [planRes, analysisRes, classesRes, schedulesRes] = await Promise.all([
                lessonPlansAPI.getById(id),
                lessonAnalysisAPI.getStatus(id).catch(() => ({ data: null })),
                studentAPI.getClasses(true).catch(() => ({ data: [] })),
                homeworkScheduleAPI.getAll(id).catch(() => ({ data: [] }))
            ]);
            setLessonPlan(planRes.data);
            setAnalysis(analysisRes.data);
            setExistingClasses(Array.isArray(classesRes.data) ? classesRes.data : []);
            setAssignedSchedules(Array.isArray(schedulesRes.data) ? schedulesRes.data : []);
        } catch (err) {
            setError('Failed to fetch lesson plan details.');
        } finally {
            setLoading(false);
            setClassesLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    const normalizeBucketTag = useCallback((value = '') => value.toString().trim().toUpperCase(), []);

    const getDocsForBucket = useCallback((tag) => {
        if (!lessonPlan?.documents) return [];
        const normalizedTag = normalizeBucketTag(tag);

        return lessonPlan.documents.filter((doc) => {
            const rawTag = (doc?.lesson_tag || '').toString();
            const bucketName = rawTag.includes(':') ? rawTag.split(':')[0] : rawTag;
            return normalizeBucketTag(bucketName) === normalizedTag;
        });
    }, [lessonPlan, normalizeBucketTag]);

    const parsedLessonContent = useMemo(() => {
        const parsed = analysis?.parsedContent;
        if (!parsed) return {};
        return parsed;
    }, [analysis]);

    const lessonStates = useMemo(() => {
        return analysis?.lessonStates || {};
    }, [analysis]);


    const getLessonContentEntry = useCallback((lessonTag) => {
        if (!parsedLessonContent) return null;

        if (parsedLessonContent instanceof Map) {
            return parsedLessonContent.get(lessonTag)
                || parsedLessonContent.get(lessonTag.toLowerCase())
                || parsedLessonContent.get(lessonTag.toUpperCase())
                || null;
        }

        return parsedLessonContent?.[lessonTag]
            || parsedLessonContent?.[lessonTag.toLowerCase()]
            || parsedLessonContent?.[lessonTag.toUpperCase()]
            || null;
    }, [parsedLessonContent]);

    const getLessonStateEntry = useCallback((lessonTag) => {
        if (!lessonStates) return null;

        if (lessonStates instanceof Map) {
            return lessonStates.get(lessonTag)
                || lessonStates.get(lessonTag.toLowerCase())
                || lessonStates.get(lessonTag.toUpperCase())
                || null;
        }

        return lessonStates?.[lessonTag]
            || lessonStates?.[lessonTag.toLowerCase()]
            || lessonStates?.[lessonTag.toUpperCase()]
            || null;
    }, [lessonStates]);

    const assignedClassCards = useMemo(() => {
        return (assignedSchedules || []).map((schedule) => {
            const scheduleId = schedule?._id;
            const className = schedule?.name || 'Unnamed Class';
            const yearGroup = schedule?.yearGroup || 'Year Group N/A';
            const subject = schedule?.subject || 'Subject N/A';

            return {
                scheduleId,
                className,
                yearGroup,
                subject
            };
        }).filter((item) => item.scheduleId);
    }, [assignedSchedules]);

    const getLessonGenerationInfo = useCallback((lessonTag) => {
        const lessonDocs = getDocsForBucket(lessonTag);
        const docCount = lessonDocs.length;

        if (docCount === 0) {
            return {
                hasDocuments: false,
                hasAnalysis: false,
                needsUpdate: false,
                isRunning: false,
                buttonLabel: 'Generate Lesson ✨',
                disabled: true
            };
        }

        const content = getLessonContentEntry(lessonTag);
        const stateForLesson = getLessonStateEntry(lessonTag);

        const hasDirectLessonAnalysis = Boolean(content?.learningObjectives)
            || (Array.isArray(content?.objectives) && content.objectives.length > 0);

        const hasCustomAggregateAnalysis = useSingleLessonUploadFlow && normalizeBucketTag(lessonTag) === 'LESSON PLAN' && (() => {
            const parsedEntries = parsedLessonContent instanceof Map
                ? Array.from(parsedLessonContent.values())
                : Object.values(parsedLessonContent || {});

            const hasParsedObjectives = parsedEntries.some((entry) => {
                return Boolean(entry?.learningObjectives)
                    || (Array.isArray(entry?.objectives) && entry.objectives.length > 0);
            });

            if (hasParsedObjectives) return true;

            const stateEntries = lessonStates instanceof Map
                ? Array.from(lessonStates.values())
                : Object.values(lessonStates || {});

            return stateEntries.some((entry) => entry?.status === 'COMPLETED');
        })();

        const hasAnalysis = hasDirectLessonAnalysis || hasCustomAggregateAnalysis;

        const analyzedReferenceAt = stateForLesson?.lastAnalyzedAt
            || content?.lastAnalyzedAt
            || analysis?.completedAt
            || analysis?.updatedAt
            || analysis?.createdAt
            || null;

        const needsUpdate = hasAnalysis && analyzedReferenceAt
            ? lessonDocs.some((doc) => {
                const uploadedAtMs = new Date(doc.uploaded_at).getTime();
                const analyzedAtMs = new Date(analyzedReferenceAt).getTime();
                if (!Number.isFinite(uploadedAtMs) || !Number.isFinite(analyzedAtMs)) return false;
                return uploadedAtMs > analyzedAtMs;
            })
            : false;

        const isRunning = Boolean(lessonGenerationQueue[lessonTag]);

        const hasFailed = analysis?.status === 'FAILED';

        let buttonLabel = 'Generate Lesson ✨';
        let disabled = isRunning;

        if (isRunning) {
            buttonLabel = 'Generating...';
        } else if (hasFailed && !hasAnalysis) {
            buttonLabel = 'Retry Generation ↺';
        } else if (hasAnalysis && needsUpdate) {
            buttonLabel = 'Update Lesson ✨';
        } else if (hasAnalysis && !needsUpdate) {
            buttonLabel = 'Lesson Up To Date ✨';
            disabled = true;
        }

        return {
            hasDocuments: true,
            hasAnalysis,
            needsUpdate,
            isRunning,
            buttonLabel,
            disabled
        };
    }, [analysis, getDocsForBucket, getLessonContentEntry, getLessonStateEntry, lessonGenerationQueue, lessonStates, normalizeBucketTag, parsedLessonContent, useSingleLessonUploadFlow]);

    // +++ POLLING +++
    useEffect(() => {
        let interval;
        const areDocumentsProcessing = lessonPlan?.documents?.some(doc => 
            ['UPLOADED', 'EXTRACTING', 'PROCESSING'].includes(doc.status)
        );
        const shouldPollAnalysis = analysis && ['PENDING', 'PROCESSING'].includes(analysis.status);

        const pollData = async () => {
            try {
                const promises = [];
                if (shouldPollAnalysis || !analysis) {
                    promises.push(lessonAnalysisAPI.getStatus(id).catch(() => ({ data: null })));
                } else {
                    promises.push(Promise.resolve({ data: null }));
                }
                
                if (areDocumentsProcessing) {
                    promises.push(lessonPlansAPI.getById(id).catch(() => ({ data: null })));
                } else {
                    promises.push(Promise.resolve({ data: null }));
                }

                const [analysisRes, planRes] = await Promise.all(promises);
                
                if (planRes?.data) setLessonPlan(planRes.data);

                if (analysisRes?.data) {
                    setAnalysis(analysisRes.data);

                    if (Object.keys(lessonGenerationQueue).length > 0 && ['COMPLETED', 'FAILED'].includes(analysisRes.data.status)) {
                        setLessonGenerationQueue({});
                    }
                    
                }
            } catch (err) { console.error("Poll failed:", err); }
        };

        if ((areDocumentsProcessing || shouldPollAnalysis) && !interval) {
            interval = setInterval(pollData, 3000);
        }
        return () => clearInterval(interval);
    }, [analysis, id, lessonPlan, lessonGenerationQueue]); 

    const hasDocuments = (lessonPlan?.documents?.length ?? 0) > 0;
    const isAnalysisComplete = analysis?.status === 'COMPLETED';
    const hasSchedules = assignedSchedules.length > 0;

    const isAnalysisRunning = analysis?.status === 'PROCESSING' || analysis?.status === 'PENDING';
    const areDocumentsReady = hasDocuments && !lessonPlan?.documents?.some(
        doc => ['UPLOADED', 'EXTRACTING', 'PROCESSING'].includes(doc.status)
    );

    const guideKey = (step) => `lp_${isDemoMode ? 'lite' : 'standard'}_${step}`;

    useEffect(() => {
        if (loading || !isProgressiveGuideLoginEligible(guideUserId)) return;
        if (!hasDocuments && !hasShownThisSession(guideKey(1))) {
            markShownThisSession(guideKey(1));
            setGuideConfig({ isOpen: true, mode: 'auto', autoStep: 1 });
        } else if (areDocumentsReady && !isAnalysisComplete && !isAnalysisRunning && !hasShownThisSession(guideKey(3))) {
            markShownThisSession(guideKey(3));
            setGuideConfig({ isOpen: true, mode: 'auto', autoStep: 3 });
        } else if (isAnalysisComplete && !hasSchedules && !hasShownThisSession(guideKey(4))) {
            markShownThisSession(guideKey(4));
            setGuideConfig({ isOpen: true, mode: 'auto', autoStep: 4 });
        }
    }, [loading, hasDocuments, areDocumentsReady, isAnalysisComplete, isAnalysisRunning, hasSchedules, isDemoMode, guideUserId]);

    const handleGuideClose = () => {
        setGuideConfig({ isOpen: false, mode: 'manual', autoStep: null });
    };

    // +++ BUTTON HANDLER +++
    const handleGenerateScheduleClick = async () => {
        const hasDocuments = lessonPlan?.documents && lessonPlan.documents.length > 0;

        if (!hasDocuments) {
            alert('Please upload at least one document (e.g., Lesson Material) before creating a schedule.');
            return;
        }

        const lessonTags = useSingleLessonUploadFlow
            ? ['Lesson Plan']
            : Array.from({ length: lessonPlan?.number_of_lessons || 0 }, (_, i) => `Lesson ${i + 1}`);
        const hasAtLeastOneGeneratedLesson = lessonTags.some((tag) => {
            const info = getLessonGenerationInfo(tag);
            return info.hasDocuments && info.hasAnalysis;
        });

        if (!hasAtLeastOneGeneratedLesson) {
            alert('Please generate at least one lesson using the individual "Generate Lesson ✨" buttons before creating/assigning a class.');
            return;
        }

        setIsScheduleModalOpen(true);
    };


    const handleGenerateSingleLesson = async (lessonTag) => {
        const info = getLessonGenerationInfo(lessonTag);
        if (!info.hasDocuments || info.isRunning) {
            return;
        }

        setLessonGenerationQueue(prev => ({ ...prev, [lessonTag]: true }));

        try {
            setAnalysis(prev => ({ ...(prev || {}), status: 'PENDING' }));
            const customPromptKey = isNacroUser ? selectedNacroPrompt : null;
            await lessonAnalysisAPI.triggerAnalysis(id, 'gemini-2.5-flash', lessonTag, null, customPromptKey);
            await fetchData();
        } catch (err) {
            console.error('Failed to generate lesson analysis', err);
            alert(`Failed to generate analysis for ${lessonTag}.`);
            setLessonGenerationQueue(prev => {
                const next = { ...prev };
                delete next[lessonTag];
                return next;
            });
        }
    };

    // +++ ADD LESSON HANDLER +++
    const handleAddLesson = async () => {
        try {
            const newCount = (lessonPlan?.number_of_lessons || 0) + 1;
            await lessonPlansAPI.update(id, { numberOfLessons: newCount });
            await fetchData();
        } catch (err) {
            console.error('Failed to add lesson:', err);
            setError('Failed to add lesson.');
        }
    };

    // +++ MODAL SUBMIT HANDLER +++
    const performScheduleCreation = async (formData) => {
        try {
            const { sourceScheduleId, ...schedulePayload } = formData;

            const res = await homeworkScheduleAPI.create({
                ...schedulePayload,
                lessonPlanId: id
            });

            const newScheduleId = res?.data?._id;

            if (!newScheduleId) {
                throw new Error('Schedule ID missing from create response.');
            }

            if (sourceScheduleId) {
                try {
                    await studentAPI.addExistingClassToSchedule(newScheduleId, sourceScheduleId);
                } catch (copyError) {
                    console.error('Existing class copy failed', copyError);
                    alert('Class created, but we could not copy students from the selected existing class. You can add them from the class page.');
                }
            }

            navigate(getClassRoute(newScheduleId, isDemoMode));
        } catch (err) {
            console.error('Schedule creation failed', err);
            setError('Failed to create class.');
            alert('Failed to create class.');
        }
    };

    if (loading && !lessonPlan) return <div style={{padding:'40px', textAlign:'center', fontFamily:'Nunito, sans-serif'}}>Loading...</div>;
    if (error) return <p style={{ color: 'red' }}>{error}</p>;

    return (
        <div style={{ 
            fontFamily: "'Inter', sans-serif",
            backgroundColor: BRAND.cream,
            minHeight: '100vh',
            color: BRAND.charcoal,
            position: 'relative', 
            overflow: 'hidden'    
        }}>
            {/* GLOBAL STYLES */}
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Nunito:wght@700;800&display=swap');
                    
                    h1, h2, h3, h4 { font-family: 'Nunito', sans-serif; }

                    .pattern-bg {
                        background-image: radial-gradient(${BRAND.teal} 15%, transparent 16%), radial-gradient(${BRAND.coral} 15%, transparent 16%);
                        background-size: 60px 60px;
                        background-position: 0 0, 30px 30px;
                        opacity: 0.1;
                        position: absolute;
                        top: 0; left: 0; right: 0; bottom: 0;
                        z-index: 0;
                        pointer-events: none;
                        mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
                        -webkit-mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
                    }

                    .dash-card {
                        background: ${BRAND.white};
                        border-radius: 16px;
                        border: 2px solid ${BRAND.charcoal};
                        box-shadow: 4px 4px 0px rgba(45, 64, 66, 0.1);
                        transition: transform 0.2s ease, box-shadow 0.2s ease;
                        overflow: hidden;
                    }
                    
                    .dash-input {
                        padding: 12px 16px;
                        border-radius: 10px;
                        border: 2px solid #E0E0E0;
                        background-color: #F8F9FA;
                        width: 100%;
                        box-sizing: border-box;
                        transition: all 0.2s;
                        outline: none;
                        font-family: 'Inter', sans-serif;
                        font-weight: 500;
                        font-size: 0.95rem;
                        color: ${BRAND.charcoal};
                    }
                    .dash-input:focus {
                        background-color: white;
                        border-color: ${BRAND.teal};
                    }

                    .btn-base {
                        border: none;
                        border-radius: 10px;
                        padding: 10px 20px;
                        font-family: 'Nunito', sans-serif;
                        font-weight: 800;
                        cursor: pointer;
                        transition: all 0.1s ease;
                        font-size: 0.95rem;
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        border-bottom: 4px solid rgba(0,0,0,0.1); 
                    }
                    .btn-base:active {
                        transform: translateY(2px);
                        border-bottom: 2px solid rgba(0,0,0,0.1);
                        margin-bottom: 2px;
                    }
                    .btn-base:disabled {
                        opacity: 0.6; cursor: not-allowed;
                    }

                    .btn-teal { background-color: ${BRAND.teal}; color: white; }
                    .btn-coral { background-color: ${BRAND.coral}; color: white; }
                    
                    .btn-logout {
                        background-color: white;
                        color: ${BRAND.charcoal};
                        border: 2px solid ${BRAND.charcoal};
                        border-bottom: 4px solid ${BRAND.charcoal}; /* Matches card borders */
                        padding: 6px 16px; /* Slightly smaller height */
                        font-size: 0.85rem;
                    }
                    .btn-logout:active {
                        border-bottom: 2px solid ${BRAND.charcoal};
                    }
                    .btn-logout:hover {
                        background-color: #F9F9F9;
                    }

                    /* User Guide toggle — hidden below breakpoint */
                    @media (max-width: 899px) {
                        .user-guide-label { display: none !important; }
                    }
                    .toggle-switch { position: relative; display: inline-block; width: 44px; height: 24px; }
                    .toggle-switch input { opacity: 0; width: 0; height: 0; }
                    .slider {
                        position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0;
                        background-color: #ccc; border-radius: 24px; transition: .3s;
                    }
                    .slider:before {
                        position: absolute; content: ''; height: 18px; width: 18px;
                        left: 3px; bottom: 3px; background-color: white;
                        border-radius: 50%; transition: .3s;
                    }
                    input:checked + .slider { background-color: ${BRAND.teal}; }
                    input:checked + .slider:before { transform: translateX(20px); }

                    /* PULSING LOADING BAR */
                    .progress-bar-container {
                        width: 100%;
                        height: 8px;
                        background-color: #eee;
                        border-radius: 4px;
                        overflow: hidden;
                        margin-top: 10px;
                    }
                    .progress-bar-fill {
                        height: 100%;
                        background-color: ${BRAND.teal};
                        animation: progress-pulse 1.5s infinite ease-in-out;
                        width: 60%; 
                        border-radius: 4px;
                    }
                    @keyframes progress-pulse {
                        0% { width: 40%; transform: translateX(-10%); }
                        50% { width: 70%; transform: translateX(20%); }
                        100% { width: 40%; transform: translateX(100%); }
                    }
                `}
            </style>

            <div className="pattern-bg"></div>

            {/* +++ MODAL +++ */}
            <ScheduleCreationModal
                isOpen={isScheduleModalOpen}
                onClose={() => { setIsScheduleModalOpen(false); }}
                onSubmit={performScheduleCreation}
                existingClasses={existingClasses}
                classesLoading={classesLoading}
                lessonPlanYearGroup={lessonPlan?.yearGroup}
                lessonPlanSubject={lessonPlan?.subject}
            />

            {guideConfig.isOpen && (
                <LessonPlanGuideOverlay
                    onClose={handleGuideClose}
                    mode={guideConfig.mode}
                    autoStep={guideConfig.autoStep}
                    hasDocuments={hasDocuments}
                    isAnalysisComplete={isAnalysisComplete}
                    hasSchedules={hasSchedules}
                    anonymousProfiles={isDemoMode}
                />
            )}

            <div style={{ position: 'relative', zIndex: 1, padding: '40px 20px', maxWidth: '1000px', margin: '0 auto' }}>
                
                {/* NAV HEADER */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                    <Link to="/demo/dashboard" style={{ textDecoration: 'none' }}>
                        <button className="btn-base" style={{ background: 'white', color: BRAND.charcoal, border: `2px solid ${BRAND.charcoal}` }}>
                            ← Back to Dashboard
                        </button>
                    </Link>
                    
                    <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
                        <label className="user-guide-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '0.85rem', color: BRAND.charcoal }}>
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={guideConfig.isOpen}
                                    onChange={() => {
                                        if (guideConfig.isOpen) {
                                            handleGuideClose();
                                        } else {
                                            setGuideConfig({ isOpen: true, mode: 'manual', autoStep: null });
                                        }
                                    }}
                                />
                                <span className="slider"></span>
                            </label>
                            User Guide
                        </label>

                        <button onClick={onLogout} className="btn-base btn-logout">
                            Log Out
                        </button>
                    </div>
                </div>

                <div style={{ marginBottom: '30px' }}>
                    <h1 style={{ margin: '0 0 5px 0', color: BRAND.charcoal, fontWeight: '800', fontSize: '2.5rem' }}>Lesson Plan: {lessonPlan?.lesson_plan_name}</h1>
                    <p style={{ margin: 0, color: '#5D7375', fontSize: '1.1rem', fontWeight: '500' }}>
                        {lessonPlan?.number_of_lessons} Lessons • Created on {new Date(lessonPlan?.createdAt || Date.now()).toLocaleDateString()}
                    </p>
                </div>

                <h2 style={{ color: BRAND.charcoal, borderBottom: '3px solid #eee', paddingBottom: '10px', marginBottom: '25px', fontWeight: '800' }}>Class Management</h2>

                <div data-guide="lp-analysis" className="dash-card" style={{ padding: '24px', marginBottom: '30px', background: '#fff' }}>
                    <div style={{ marginBottom: '20px' }}>
                        <button
                            data-guide="lp-create-schedule"
                            className="btn-base btn-coral"
                            onClick={handleGenerateScheduleClick}
                            style={{ fontSize: '1.05rem', padding: '12px 24px' }}
                        >
                            Create/Assign Class to Lesson Plan ✨
                        </button>
                    </div>

                    <div style={{ maxWidth: '760px', textAlign: 'left' }}>
                        <div style={{ fontWeight: '800', fontSize: '0.82em', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '10px', color: '#5D7375' }}>
                            Assigned Classes for this Lesson Plan
                        </div>
                        {assignedClassCards.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                {assignedClassCards.map((klass) => (
                                    <button
                                        key={klass.scheduleId}
                                        className="btn-base"
                                        onClick={() => navigate(getClassRoute(klass.scheduleId, isDemoMode))}
                                        style={{
                                            background: BRAND.teal,
                                            color: 'white',
                                            border: `2px solid ${BRAND.charcoal}`,
                                            borderBottom: `4px solid ${BRAND.charcoal}`,
                                            justifyContent: 'space-between',
                                            textAlign: 'left',
                                            padding: '12px 14px',
                                            width: '190px',
                                            minWidth: '190px'
                                        }}
                                        title="Open class schedule"
                                    >
                                        <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                            <span style={{ fontWeight: '800', fontFamily: 'Nunito, sans-serif' }}>{klass.className}</span>
                                        </span>
                                        <span style={{ fontWeight: '800' }}>Open →</span>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <p style={{ margin: 0, color: '#64748B', fontStyle: 'italic' }}>No classes assigned to this lesson plan yet.</p>
                        )}
                    </div>
                </div>

                <h2 data-guide="lp-documents" style={{ color: BRAND.charcoal, borderBottom: '3px solid #eee', paddingBottom: '10px', marginBottom: '25px', fontWeight: '800' }}>Document Management</h2>

                <div data-guide="lp-analyze-btn" style={{ display: 'grid', gap: '25px' }}>
                    {useSingleLessonUploadFlow ? (
                        (() => {
                            const generationInfo = getLessonGenerationInfo('Lesson Plan');
                            const lessonPlanDocs = getDocsForBucket('LESSON PLAN');
                            const isSingleDocumentLimitReached = hasCustomUploadPortal && lessonPlanDocs.length >= 1;

                            return (
                        <>
                        {isNacroUser && (
                            <div className="dash-card" style={{ padding: '20px', background: '#fff', marginBottom: '0' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.82rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#5D7375' }}>
                                    NACRO Prompt
                                </label>
                                <select
                                    value={selectedNacroPrompt}
                                    onChange={(event) => setSelectedNacroPrompt(event.target.value)}
                                    style={{
                                        width: '100%',
                                        maxWidth: '420px',
                                        padding: '10px 12px',
                                        borderRadius: '10px',
                                        border: '2px solid #2D4042',
                                        fontWeight: '700',
                                        color: '#2D4042',
                                        background: '#F9FAFB'
                                    }}
                                >
                                    {NACRO_PROMPT_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <LessonUploader 
                            bucketTitle="Lesson Plan" 
                            bucketTag="Lesson Plan" 
                            lessonPlanId={id} 
                            onUploadComplete={fetchData} 
                            uploadedDocuments={lessonPlanDocs} 
                            isDemoMode={isDemoMode}
                            singleDocumentOnly={hasCustomUploadPortal}
                            singleDocumentLimitReached={isSingleDocumentLimitReached}
                            actionButton={{
                                label: generationInfo.buttonLabel,
                                disabled: generationInfo.disabled || !generationInfo.hasDocuments,
                                onClick: () => handleGenerateSingleLesson('Lesson Plan'),
                                title: generationInfo.hasDocuments ? '' : 'Upload documents to enable lesson generation.'
                            }}
                            showActionProgress={generationInfo.isRunning}
                                                   />
                        </>
                            );
                        })()
                    ) : (
                        <>
                            {Array.from({ length: lessonPlan?.number_of_lessons || 0 }, (_, i) => {
                                const lessonTag = `Lesson ${i+1}`;
                                const generationInfo = getLessonGenerationInfo(lessonTag);

                                return (
                                    <LessonUploader
                                        key={i+1}
                                        bucketTitle={`Documents for Lesson ${i+1}`}
                                        bucketTag={lessonTag}
                                        lessonPlanId={id}
                                        onUploadComplete={fetchData}
                                        uploadedDocuments={getDocsForBucket(`LESSON ${i+1}`)}
                                        isDemoMode={isDemoMode}
                                        actionButton={{
                                            label: generationInfo.buttonLabel,
                                            disabled: generationInfo.disabled || !generationInfo.hasDocuments,
                                            onClick: () => handleGenerateSingleLesson(lessonTag),
                                            title: generationInfo.hasDocuments ? '' : 'Upload documents to enable lesson generation.'
                                        }}
                                        showActionProgress={generationInfo.isRunning}
                                    />
                                );
                            })}
                            <div style={{ display: 'flex', justifyContent: 'center', margin: '8px 0' }}>
                                <button onClick={handleAddLesson} className="btn-base btn-teal">
                                    + Add Lesson
                                </button>
                            </div>
                            <LessonUploader bucketTitle="Additional Resources" bucketTag="Additional Resources" lessonPlanId={id} onUploadComplete={fetchData} uploadedDocuments={getDocsForBucket("ADDITIONAL RESOURCES")} isDemoMode={isDemoMode} />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DemoLessonPlanDetail;
